import { useState, useRef } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle,
  FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography, Switch, FormControlLabel, Divider, Stack, Tooltip
} from '@mui/material';
import { Add, Edit, Delete, Download, Upload } from '@mui/icons-material';
import { adminAPI } from '../../services/api';
import { downloadBlob, readFileAsText } from './csvUtils';

const EMPTY_CAT = { category_name: '', type: 'room', per_day_charge: '', description: '' };
const EMPTY_ROOM = { room_number: '', category_id: '', max_capacity: 2, first_patient_charge: '', second_patient_charge: '', third_patient_charge: '', is_active: true };
const CATEGORY_TYPES = ['room', 'cottage', 'ward', 'hotel'];

export default function RoomsTab({ rooms, categories, onRefresh, showSnackbar }) {
  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState(EMPTY_CAT);
  const [catEditId, setCatEditId] = useState(null);
  const [catDeleteId, setCatDeleteId] = useState(null);

  const [roomOpen, setRoomOpen] = useState(false);
  const [roomForm, setRoomForm] = useState(EMPTY_ROOM);
  const [roomEditId, setRoomEditId] = useState(null);
  const [roomDeleteId, setRoomDeleteId] = useState(null);

  const [saving, setSaving] = useState(false);
  const catUploadRef = useRef();
  const roomUploadRef = useRef();

  // ── Category handlers ──────────────────────────────────────────────────────
  const openAddCat = () => { setCatForm(EMPTY_CAT); setCatEditId(null); setCatOpen(true); };
  const openEditCat = (row) => {
    setCatForm({ category_name: row.category_name, type: row.type || 'room', per_day_charge: row.per_day_charge || '', description: row.description || '' });
    setCatEditId(row.id); setCatOpen(true);
  };
  const handleSaveCat = async () => {
    if (!catForm.category_name) { showSnackbar('Category name is required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...catForm, per_day_charge: parseFloat(catForm.per_day_charge) || 0 };
      if (catEditId) { await adminAPI.updateRoomCategory(catEditId, payload); showSnackbar('Category updated'); }
      else { await adminAPI.createRoomCategory(payload); showSnackbar('Category created'); }
      setCatOpen(false); onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };
  const handleDeleteCat = async () => {
    try {
      await adminAPI.deleteRoomCategory(catDeleteId);
      showSnackbar('Category deleted'); setCatDeleteId(null); onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Delete failed', 'error'); setCatDeleteId(null); }
  };

  // ── Room handlers ──────────────────────────────────────────────────────────
  const openAddRoom = () => { setRoomForm(EMPTY_ROOM); setRoomEditId(null); setRoomOpen(true); };
  const openEditRoom = (row) => {
    setRoomForm({ room_number: row.room_number, category_id: row.category_id, max_capacity: row.max_capacity || 2, first_patient_charge: row.first_patient_charge || '', second_patient_charge: row.second_patient_charge || '', third_patient_charge: row.third_patient_charge || '', is_active: row.is_active !== 0 });
    setRoomEditId(row.id); setRoomOpen(true);
  };
  const handleSaveRoom = async () => {
    if (!roomForm.room_number || !roomForm.category_id) { showSnackbar('Room number and category are required', 'error'); return; }
    setSaving(true);
    try {
      const payload = { ...roomForm, first_patient_charge: parseFloat(roomForm.first_patient_charge) || 0, second_patient_charge: parseFloat(roomForm.second_patient_charge) || 0, third_patient_charge: parseFloat(roomForm.third_patient_charge) || 0, is_active: roomForm.is_active ? 1 : 0 };
      if (roomEditId) { await adminAPI.updateRoom(roomEditId, payload); showSnackbar('Room updated'); }
      else { await adminAPI.createRoom(payload); showSnackbar('Room created'); }
      setRoomOpen(false); onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Save failed', 'error'); }
    finally { setSaving(false); }
  };
  const handleDeleteRoom = async () => {
    try {
      await adminAPI.deleteRoom(roomDeleteId);
      showSnackbar('Room deleted'); setRoomDeleteId(null); onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Delete failed', 'error'); setRoomDeleteId(null); }
  };

  // ── CSV handlers ───────────────────────────────────────────────────────────
  const csvDownload = async (apiFn, filename) => {
    try { const res = await apiFn(); downloadBlob(res.data, filename); }
    catch { showSnackbar('CSV download failed', 'error'); }
  };
  const csvUpload = async (e, apiFn) => {
    const file = e.target.files?.[0]; if (!file) return; e.target.value = '';
    try {
      const csv = await readFileAsText(file);
      const res = await apiFn(csv);
      const { inserted, updated, errors } = res.data.data;
      showSnackbar(`Inserted: ${inserted}, Updated: ${updated}${errors?.length ? `, Errors: ${errors.length}` : ''}`, errors?.length ? 'warning' : 'success');
      onRefresh();
    } catch (e) { showSnackbar(e.response?.data?.message || 'Upload failed', 'error'); }
  };

  return (
    <Box>
      {/* ── Room Categories ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Room Categories ({categories.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => csvDownload(adminAPI.downloadRoomCategoriesCSV, 'room_categories.csv')}>Download CSV</Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => catUploadRef.current?.click()}>Upload CSV</Button>
          <input ref={catUploadRef} type="file" accept=".csv" hidden onChange={e => csvUpload(e, adminAPI.uploadRoomCategoriesCSV)} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAddCat}>Add Category</Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell><strong>Type</strong></TableCell>
              <TableCell align="right"><strong>Per Day (₹)</strong></TableCell>
              <TableCell align="center"><strong>Rooms</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {categories.length === 0 && <TableRow><TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 3 }}>No categories found</TableCell></TableRow>}
            {categories.map(row => (
              <TableRow key={row.id} hover>
                <TableCell>{row.category_name}</TableCell>
                <TableCell><Chip label={row.type} size="small" /></TableCell>
                <TableCell align="right">{parseFloat(row.per_day_charge || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="center">{row.room_count}</TableCell>
                <TableCell sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  <Tooltip title={row.description || ''}><span>{row.description || '—'}</span></Tooltip>
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEditCat(row)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setCatDeleteId(row.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Divider sx={{ my: 3 }} />

      {/* ── Rooms ── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight="bold">Rooms ({rooms.length})</Typography>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="outlined" startIcon={<Download />} onClick={() => csvDownload(adminAPI.downloadRoomsCSV, 'rooms.csv')}>Download CSV</Button>
          <Button size="small" variant="outlined" startIcon={<Upload />} onClick={() => roomUploadRef.current?.click()}>Upload CSV</Button>
          <input ref={roomUploadRef} type="file" accept=".csv" hidden onChange={e => csvUpload(e, adminAPI.uploadRoomsCSV)} />
          <Button size="small" variant="contained" startIcon={<Add />} onClick={openAddRoom}>Add Room</Button>
        </Stack>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell><strong>Room No.</strong></TableCell>
              <TableCell><strong>Category</strong></TableCell>
              <TableCell align="center"><strong>Cap.</strong></TableCell>
              <TableCell align="center"><strong>Occupancy</strong></TableCell>
              <TableCell align="right"><strong>Single (₹)</strong></TableCell>
              <TableCell align="right"><strong>+Attendant (₹)</strong></TableCell>
              <TableCell align="right"><strong>Double (₹)</strong></TableCell>
              <TableCell align="center"><strong>Active</strong></TableCell>
              <TableCell align="center"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rooms.length === 0 && <TableRow><TableCell colSpan={9} align="center" sx={{ color: 'text.secondary', py: 3 }}>No rooms found</TableCell></TableRow>}
            {rooms.map(row => (
              <TableRow key={row.id} hover sx={{ opacity: row.is_active ? 1 : 0.5 }}>
                <TableCell><strong>{row.room_number}</strong></TableCell>
                <TableCell>{row.category_name}</TableCell>
                <TableCell align="center">{row.max_capacity}</TableCell>
                <TableCell align="center">
                  <Chip label={`${row.current_occupancy}/${row.max_capacity}`} size="small"
                    color={row.current_occupancy >= row.max_capacity ? 'error' : row.current_occupancy > 0 ? 'warning' : 'success'} />
                </TableCell>
                <TableCell align="right">{parseFloat(row.first_patient_charge || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="right">{parseFloat(row.second_patient_charge || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="right">{parseFloat(row.third_patient_charge || 0).toLocaleString('en-IN')}</TableCell>
                <TableCell align="center">
                  <Chip label={row.is_active ? 'Yes' : 'No'} size="small" color={row.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => openEditRoom(row)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => setRoomDeleteId(row.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Category Dialog */}
      <Dialog open={catOpen} onClose={() => setCatOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{catEditId ? 'Edit Category' : 'Add Room Category'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <TextField label="Category Name *" value={catForm.category_name} onChange={e => setCatForm(f => ({ ...f, category_name: e.target.value }))} fullWidth />
          <FormControl fullWidth>
            <InputLabel>Type</InputLabel>
            <Select value={catForm.type} onChange={e => setCatForm(f => ({ ...f, type: e.target.value }))} label="Type">
              {CATEGORY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField label="Per Day Charge (₹)" type="number" value={catForm.per_day_charge} onChange={e => setCatForm(f => ({ ...f, per_day_charge: e.target.value }))} fullWidth />
          <TextField label="Description" value={catForm.description} onChange={e => setCatForm(f => ({ ...f, description: e.target.value }))} multiline rows={2} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCatOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCat} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Room Dialog */}
      <Dialog open={roomOpen} onClose={() => setRoomOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{roomEditId ? 'Edit Room' : 'Add Room'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Room Number *" value={roomForm.room_number} onChange={e => setRoomForm(f => ({ ...f, room_number: e.target.value }))} fullWidth />
            <TextField label="Max Capacity" type="number" value={roomForm.max_capacity} onChange={e => setRoomForm(f => ({ ...f, max_capacity: e.target.value }))} fullWidth inputProps={{ min: 1, max: 4 }} />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Category *</InputLabel>
            <Select value={roomForm.category_id} onChange={e => setRoomForm(f => ({ ...f, category_id: e.target.value }))} label="Category *">
              {categories.map(c => <MenuItem key={c.id} value={c.id}>{c.category_name}</MenuItem>)}
            </Select>
          </FormControl>
          <Typography variant="caption" color="text.secondary">Charges per occupancy type:</Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField label="Single (₹)" type="number" value={roomForm.first_patient_charge} onChange={e => setRoomForm(f => ({ ...f, first_patient_charge: e.target.value }))} fullWidth />
            <TextField label="+Attendant (₹)" type="number" value={roomForm.second_patient_charge} onChange={e => setRoomForm(f => ({ ...f, second_patient_charge: e.target.value }))} fullWidth />
            <TextField label="Double (₹)" type="number" value={roomForm.third_patient_charge} onChange={e => setRoomForm(f => ({ ...f, third_patient_charge: e.target.value }))} fullWidth />
          </Box>
          <FormControlLabel control={<Switch checked={!!roomForm.is_active} onChange={e => setRoomForm(f => ({ ...f, is_active: e.target.checked }))} />} label="Room is active" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRoom} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!catDeleteId} onClose={() => setCatDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Category?</DialogTitle>
        <DialogContent>All rooms in this category must be removed first.</DialogContent>
        <DialogActions>
          <Button onClick={() => setCatDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteCat}>Delete</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={!!roomDeleteId} onClose={() => setRoomDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Room?</DialogTitle>
        <DialogContent>Rooms with past admission history cannot be deleted.</DialogContent>
        <DialogActions>
          <Button onClick={() => setRoomDeleteId(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteRoom}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
